import { Textarea, TextareaProps } from "@chakra-ui/react";
import React from "react";
import {
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
} from "react-hook-form";
import { FormFieldControl } from "./FormFieldControl";

type TextareaFieldProps<TFormValues extends FieldValues> = Omit<
  TextareaProps,
  "name" | "form" | "label"
> & {
  form: UseFormReturn<TFormValues>;
  name: FieldPath<TFormValues>;
  label?: React.ReactNode;
  rules?: RegisterOptions<TFormValues, FieldPath<TFormValues>>;
  helperText?: React.ReactNode;
};

/**
 * A simple multiline text field for react-hook-form.
 *
 * It wraps FormFieldControl and automatically registers the Chakra Textarea
 * with react-hook-form, including the required rule when `isRequired` is true.
 *
 * Use FormFieldControl directly when you need custom input composition,
 * such as addons, custom controls, or multiple inputs.
 */
export function TextareaField<T extends FieldValues>({
  form,
  name,
  label,
  rules,
  helperText,
  isRequired,
  isDisabled,
  ...textareaProps
}: TextareaFieldProps<T>): React.ReactElement {
  const requiredRule =
    rules?.required ??
    (isRequired ? `${label ?? "This field"} is required` : false);

  return (
    <FormFieldControl
      form={form}
      name={name}
      label={label}
      helperText={helperText}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <Textarea
        {...textareaProps}
        {...form.register(name, {
          ...rules,
          required: requiredRule,
        })}
      />
    </FormFieldControl>
  );
}
